import type { Provider } from '@angular/core';
import { DEFAULT_DIALOG_CONFIG, DialogConfig } from '@angular/cdk/dialog';
import type { BrnDialogRef } from '@spartan-ng/brain/dialog';

interface BackEntry {
  marker: number;
  orphan: boolean;
  previousState: unknown;
  dismiss: () => boolean;
}

const stack: BackEntry[] = [];
const bound = new WeakMap<BrnDialogRef<unknown>, BackEntry>();
let markerSequence = 0;
let suppressed = 0;
let listening = false;

function top(): BackEntry | undefined {
  return stack[stack.length - 1];
}

function onTopOfHistory(entry: BackEntry): boolean {
  return (window.history.state as { hlmDialogBack?: number } | null)?.hlmDialogBack === entry.marker;
}

function onPopState(): void {
  // Our own unwinding pop, not the user's Back.
  if (suppressed > 0) {
    suppressed -= 1;
    return;
  }

  const entry = stack.pop();
  if (!entry) return;
  // An orphan's dialog is already gone; the Back that reached it is spent on it.
  if (entry.orphan) return;
  // `disableClose` refuses the dismissal — the dialog outlives the record it just lost, so
  // it takes a fresh one instead of letting the next Back close whatever is under it.
  if (!entry.dismiss()) {
    stack.push(entry);
    window.history.pushState({ hlmDialogBack: entry.marker }, '');
  }
}

function unwind(entry: BackEntry): void {
  // Reused by a dialog that opened in the same turn, or already spent by a Back.
  if (top() !== entry || !onTopOfHistory(entry)) return;
  stack.pop();
  suppressed += 1;
  window.history.back();
}

// The LIFO is module state, so one test's leftover entry would decide the next test's
// verdict. Not part of the lib's public API — tests import it from this file directly.
export function resetBackCloseForTest(): void {
  stack.length = 0;
  suppressed = 0;
}

// CDK disposes EVERY open overlay on any history change, so one Back would collapse
// the whole stack. An app that uses `backCloses` hands that job to the LIFO above.
export function provideHlmDialogBackClose(): Provider {
  return { provide: DEFAULT_DIALOG_CONFIG, useValue: { ...new DialogConfig(), closeOnNavigation: false } };
}

// A host overlay with its own history entry (the gallery lightbox) must let a
// Back that unwinds a dialog stacked above it pass without closing itself.
export function isDialogBackState(state: unknown): boolean {
  return typeof (state as { hlmDialogBack?: unknown } | null)?.hlmDialogBack === 'number';
}

// A dialog that routes the page somewhere else dies WITH that page, and unwinding
// its entry afterwards would pop the very navigation that killed it. Call this
// first: the entry leaves without a trace, and the route change owns the history.
export function releaseBackClose(ref: BrnDialogRef<unknown>): void {
  const entry = bound.get(ref);
  if (!entry) return;
  entry.orphan = true;
  const at = stack.lastIndexOf(entry);
  if (at >= 0) stack.splice(at, 1);
  if (onTopOfHistory(entry)) window.history.replaceState(entry.previousState, '');
}

// One history entry per bound dialog, unwound LIFO — neither CDK nor Brn ships a Back affordance.
export function bindBackClose(ref: BrnDialogRef<unknown>): void {
  const stale = top();
  const reusable = stale?.orphan === true && onTopOfHistory(stale);
  const entry: BackEntry = {
    marker: ++markerSequence,
    orphan: false,
    previousState: reusable ? stale.previousState : window.history.state,
    dismiss: () => ref.dismiss('escape'),
  };
  bound.set(ref, entry);

  // A dialog opening straight out of another's close takes over its entry: pushing
  // a second one and unwinding the first cross each other, and the new dialog dies.
  if (reusable) {
    stack.pop();
    stack.push(entry);
    window.history.replaceState({ hlmDialogBack: entry.marker }, '');
  } else {
    stack.push(entry);
    window.history.pushState({ hlmDialogBack: entry.marker }, '');
  }

  if (!listening) {
    window.addEventListener('popstate', onPopState);
    listening = true;
  }

  // `closed$` completes on the first emission — nothing to unsubscribe. The unwind
  // waits a microtask so a successor dialog opened from this same emission wins.
  ref.closed$.subscribe(() => {
    entry.orphan = true;
    queueMicrotask(() => unwind(entry));
  });
}
